import { useId, useState } from 'react'
import type { CSSProperties, InputHTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'

import { EyeIcon, EyeOffIcon } from './icons'

/**
 * Password field with a reveal toggle.
 *
 * The `style` prop styles the input itself (each screen passes its own), so the
 * wrapper only handles the positioning of the toggle. Extra right padding keeps
 * long passwords from running under the button.
 */
interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  style?: CSSProperties
  /** Style overrides for the wrapper, e.g. when the field sits inside a flex column. */
  wrapperStyle?: CSSProperties
}

const TOGGLE_WIDTH = 36

export function PasswordInput({ style, wrapperStyle, ...props }: PasswordInputProps) {
  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const fallbackId = useId()
  const inputId = props.id ?? fallbackId

  const label = visible
    ? t('common.hidePassword', 'Ocultar senha')
    : t('common.showPassword', 'Mostrar senha')

  return (
    <div style={{ position: 'relative', display: 'flex', ...wrapperStyle }}>
      <input
        {...props}
        id={inputId}
        type={visible ? 'text' : 'password'}
        style={{
          ...style,
          width: '100%',
          boxSizing: 'border-box',
          paddingRight: TOGGLE_WIDTH,
        }}
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        aria-label={label}
        aria-pressed={visible}
        aria-controls={inputId}
        title={label}
        disabled={props.disabled}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          height: '100%',
          width: TOGGLE_WIDTH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'none',
          border: 'none',
          padding: 0,
          color: 'var(--text-muted)',
          cursor: props.disabled ? 'default' : 'pointer',
          opacity: props.disabled ? 0.5 : 1,
        }}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    </div>
  )
}
